import React, { useState, ReactElement, useRef, useEffect } from 'react';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import { createPortal } from 'react-dom';

type TooltipProps = {
    text: string;
    position?: 't' | 'tl' | 'tr' | 'b' | 'bl' | 'br' | 'l' | 'r';
    children: React.ReactNode;
};

const Tooltip: React.FC<TooltipProps> = ({
    text,
    position = 't',
    children
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [isMobile, setIsMobile] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    // Check if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.matchMedia('(max-width: 768px)').matches);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Calculate tooltip position
    const updateTooltipPosition = () => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

        let top = 0;
        let left = 0;

        switch (position) {
            case 't':
                top = triggerRect.top - tooltipRect.height - 8 + scrollTop;
                left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2 + scrollLeft;
                break;
            case 'tl':
                top = triggerRect.top - tooltipRect.height - 8 + scrollTop;
                left = triggerRect.right - tooltipRect.width + scrollLeft;
                break;
            case 'tr':
                top = triggerRect.top - tooltipRect.height - 8 + scrollTop;
                left = triggerRect.left + scrollLeft;
                break;
            case 'b':
                top = triggerRect.bottom + 8 + scrollTop;
                left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2 + scrollLeft;
                break;
            case 'bl':
                top = triggerRect.bottom + 8 + scrollTop;
                left = triggerRect.right - tooltipRect.width + scrollLeft;
                break;
            case 'br':
                top = triggerRect.bottom + 8 + scrollTop;
                left = triggerRect.left + scrollLeft;
                break;
            case 'l':
                top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2 + scrollTop;
                left = triggerRect.left - tooltipRect.width - 8 + scrollLeft;
                break;
            case 'r':
                top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2 + scrollTop;
                left = triggerRect.right + 8 + scrollLeft;
                break;
        }

        // Ensure tooltip stays within viewport
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (left < 0) left = 0;
        if (left + tooltipRect.width > viewportWidth) left = viewportWidth - tooltipRect.width;
        if (top < 0) top = 0;
        if (top + tooltipRect.height > viewportHeight) top = viewportHeight - tooltipRect.height;

        setTooltipPosition({ top, left });
    };

    // Update position on visibility change
    useEffect(() => {
        if (isVisible) {
            updateTooltipPosition();
            window.addEventListener('scroll', updateTooltipPosition);
            window.addEventListener('resize', updateTooltipPosition);
        }
        return () => {
            window.removeEventListener('scroll', updateTooltipPosition);
            window.removeEventListener('resize', updateTooltipPosition);
        };
    }, [isVisible]);

    const handleInteraction = () => {
        if (isMobile) {
            setIsVisible(!isVisible);
        }
    };

    const handleMouseEnter = () => {
        if (!isMobile) {
            setIsVisible(true);
        }
    };

    const handleMouseLeave = () => {
        if (!isMobile) {
            setIsVisible(false);
        }
    };

    // Close tooltip when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isMobile && isVisible && 
                tooltipRef.current && 
                !tooltipRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)) {
                setIsVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMobile, isVisible]);

    return (
        <div 
            ref={triggerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleInteraction}
            className="inline-block"
        >
            {children}
            {createPortal(
                <div
                    ref={tooltipRef}
                    style={{
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                    }}
                    className={`fixed z-50 bg-gray-700 text-white  px-3 py-2 rounded-md text-sm shadow-lg max-w-xs whitespace-normal break-words transition-all duration-200 ease-in-out transform ${
                        isVisible 
                            ? 'opacity-100 translate-y-0' 
                            : 'opacity-0 translate-y-1 pointer-events-none'
                    }`}
                >
                    {text}
                </div>,
                document.body
            )}
        </div>
    );
};

export default Tooltip;